import React from 'react';

// Enums
export enum ReportPeriod {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

export enum Department {
  ALL = 'all',
  ACADEMICS = 'academics',
  TRANSPORT = 'transport',
  HOSTEL = 'hostel',
  ADMIN = 'admin',
}

export enum ReportTab {
  FEE_COLLECTION = 0,
  EXPENSES = 1,
  SCHOLARSHIPS = 2,
  BUDGET_VS_ACTUAL = 3,
  COMPLIANCE = 4,
  AUDIT = 5,
}

export enum PaymentStatus {
  PAID = 'paid',
  PARTIALLY_PAID = 'partially_paid',
  UNPAID = 'unpaid',
}

export enum ScholarshipStatus {
  DISBURSED = 'disbursed',
  APPROVED = 'approved',
  PROCESSING = 'processing',
  REJECTED = 'rejected',
}

export enum BudgetStatus {
  UNDER_BUDGET = 'under_budget',
  OVER_BUDGET = 'over_budget',
  ON_BUDGET = 'on_budget',
}

// Data Interfaces
export interface SummaryData {
  totalIncomeYTD: number;
  totalIncomeMonthly: number;
  totalExpenditureYTD: number;
  totalExpenditureMonthly: number;
  netSurplus: number;
  outstandingFeesCount: number;
  outstandingFeesAmount: number;
  scholarshipsDisbursed: number;
}

export interface PendingFee {
  id: string;
  studentName: string;
  class: string;
  amountDue: number;
  dueDate: string;
  daysOverdue: number;
  status: PaymentStatus;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  amount: number;
  color: string;
}

export interface Scholarship {
  id: string;
  studentName: string;
  class: string;
  scholarshipType: string;
  amount: number;
  dateAwarded: string;
  status: ScholarshipStatus;
}

export interface BudgetItem {
  id: string;
  category: string;
  budgetedAmount: number;
  actualSpent: number;
  variance: number;
  variancePercentage: number;
  status: BudgetStatus;
}

export interface ComplianceReport {
  id: string;
  reportType: string;
  title: string;
  description: string;
  lastUpdated: string;
  downloadUrl?: string;
}

export interface AuditReport {
  id: string;
  reportType: string;
  title: string;
  description: string;
  academicYear: string;
  isAvailable: boolean;
  generateUrl?: string;
}

// Component Props Interfaces
export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactElement;
  trend?: number;
}

// Filter State Interface
export interface FilterState {
  academicYear: string;
  reportPeriod: ReportPeriod;
  department: Department;
  searchQuery: string;
}

// Context State Interface
export interface FinancialReportsState {
  // UI State
  selectedTab: ReportTab;
  filters: FilterState;
  isLoading: boolean;
  error: string | null;

  // Data State
  summaryData: SummaryData;
  pendingFees: PendingFee[];
  expenseCategories: ExpenseCategory[];
  scholarships: Scholarship[];
  budgetItems: BudgetItem[];
  complianceReports: ComplianceReport[];
  auditReports: AuditReport[];
}

// Action Types
export enum FinancialReportsActionType {
  SET_SELECTED_TAB = 'SET_SELECTED_TAB',
  SET_ACADEMIC_YEAR = 'SET_ACADEMIC_YEAR',
  SET_REPORT_PERIOD = 'SET_REPORT_PERIOD',
  SET_DEPARTMENT = 'SET_DEPARTMENT',
  SET_SEARCH_QUERY = 'SET_SEARCH_QUERY',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_SUMMARY_DATA = 'SET_SUMMARY_DATA',
  SET_PENDING_FEES = 'SET_PENDING_FEES',
  SET_EXPENSE_CATEGORIES = 'SET_EXPENSE_CATEGORIES',
  SET_SCHOLARSHIPS = 'SET_SCHOLARSHIPS',
  SET_BUDGET_ITEMS = 'SET_BUDGET_ITEMS',
  SET_COMPLIANCE_REPORTS = 'SET_COMPLIANCE_REPORTS',
  SET_AUDIT_REPORTS = 'SET_AUDIT_REPORTS',
  RESET_FILTERS = 'RESET_FILTERS',
}

// Action Interfaces
export interface SetSelectedTabAction {
  type: FinancialReportsActionType.SET_SELECTED_TAB;
  payload: ReportTab;
}

export interface SetAcademicYearAction {
  type: FinancialReportsActionType.SET_ACADEMIC_YEAR;
  payload: string;
}

export interface SetReportPeriodAction {
  type: FinancialReportsActionType.SET_REPORT_PERIOD;
  payload: ReportPeriod;
}

export interface SetDepartmentAction {
  type: FinancialReportsActionType.SET_DEPARTMENT;
  payload: Department;
}

export interface SetSearchQueryAction {
  type: FinancialReportsActionType.SET_SEARCH_QUERY;
  payload: string;
}

export interface SetLoadingAction {
  type: FinancialReportsActionType.SET_LOADING;
  payload: boolean;
}

export interface SetErrorAction {
  type: FinancialReportsActionType.SET_ERROR;
  payload: string | null;
}

export interface SetSummaryDataAction {
  type: FinancialReportsActionType.SET_SUMMARY_DATA;
  payload: SummaryData;
}

export interface SetPendingFeesAction {
  type: FinancialReportsActionType.SET_PENDING_FEES;
  payload: PendingFee[];
}

export interface SetExpenseCategoriesAction {
  type: FinancialReportsActionType.SET_EXPENSE_CATEGORIES;
  payload: ExpenseCategory[];
}

export interface SetScholarshipsAction {
  type: FinancialReportsActionType.SET_SCHOLARSHIPS;
  payload: Scholarship[];
}

export interface SetBudgetItemsAction {
  type: FinancialReportsActionType.SET_BUDGET_ITEMS;
  payload: BudgetItem[];
}

export interface SetComplianceReportsAction {
  type: FinancialReportsActionType.SET_COMPLIANCE_REPORTS;
  payload: ComplianceReport[];
}

export interface SetAuditReportsAction {
  type: FinancialReportsActionType.SET_AUDIT_REPORTS;
  payload: AuditReport[];
}

export interface ResetFiltersAction {
  type: FinancialReportsActionType.RESET_FILTERS;
}

export type FinancialReportsAction =
  | SetSelectedTabAction
  | SetAcademicYearAction
  | SetReportPeriodAction
  | SetDepartmentAction
  | SetSearchQueryAction
  | SetLoadingAction
  | SetErrorAction
  | SetSummaryDataAction
  | SetPendingFeesAction
  | SetExpenseCategoriesAction
  | SetScholarshipsAction
  | SetBudgetItemsAction
  | SetComplianceReportsAction
  | SetAuditReportsAction
  | ResetFiltersAction;

// Context Interface
export interface FinancialReportsContextType {
  state: FinancialReportsState;
  dispatch: React.Dispatch<FinancialReportsAction>;

  // Action Creators
  setSelectedTab: (tab: ReportTab) => void;
  setAcademicYear: (year: string) => void;
  setReportPeriod: (period: ReportPeriod) => void;
  setDepartment: (department: Department) => void;
  setSearchQuery: (query: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetFilters: () => void;

  // Utility Functions
  formatCurrency: (amount: number) => string;
  exportReport: (reportType: string, format: string) => Promise<void>;
  sendReportEmail: (reportType: string, recipients: string[]) => Promise<void>;
  printReport: (reportType: string) => void;
}
